import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'
import type { StorefrontContentPayload } from './storeService'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; error?: { code?: string }; data?: unknown }
  if (!b?.success) {
    const err = new Error(sanitizeBusinessMessage(b?.message, 'Request failed')) as Error & { code?: string }
    err.code = b.error?.code
    throw err
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type StorefrontAiHeroPatch = {
  title: string
  subtitle: string
  ctaText: string
  badge: string | null
  ctaHref: string
}

export type StorefrontAiDraftResponse = {
  storefrontContent?: StorefrontContentPayload
  heroPatch?: StorefrontAiHeroPatch
}

export async function draftProfileText(field: 'tagline' | 'about'): Promise<{ text: string }> {
  try {
    const { data } = await api.post('/storebusiness/storefront/ai/profile-text', { field })
    return assertSuccess(data) as { text: string }
  } catch (e) {
    throw apiError(e, 'AI could not generate text')
  }
}

export async function draftStorefrontAi(params?: {
  promotedCategorySlug?: string | null
  scope?: 'all' | 'hero' | 'trust'
  targetSlideId?: string | null
}): Promise<StorefrontAiDraftResponse> {
  try {
    const { data } = await api.post('/storebusiness/storefront/ai/draft', {
      promotedCategorySlug: params?.promotedCategorySlug ?? null,
      scope: params?.scope ?? 'all',
      targetSlideId: params?.targetSlideId ?? null,
    })
    return assertSuccess(data) as StorefrontAiDraftResponse
  } catch (e) {
    throw apiError(e, 'AI draft failed')
  }
}

export async function startHeroImageJob(payload: {
  storeName: string
  heroTitle: string
  heroSubtitle: string
  categoryName?: string | null
  visualHints?: string | null
}): Promise<{ jobId: string }> {
  try {
    const { data } = await api.post('/storebusiness/storefront/ai/hero-image', payload)
    return assertSuccess(data) as { jobId: string }
  } catch (e) {
    throw apiError(e, 'Could not start hero image job')
  }
}

export async function startCategoryImageJob(payload: {
  categoryId: number
  categoryName: string
  categorySlug?: string | null
  storeName?: string | null
}): Promise<{ jobId: string }> {
  try {
    const { data } = await api.post('/storebusiness/storefront/ai/category-image', {
      categoryId: payload.categoryId,
      categoryName: payload.categoryName,
      categorySlug: payload.categorySlug ?? null,
      storeName: payload.storeName ?? null,
    })
    return assertSuccess(data) as { jobId: string }
  } catch (e) {
    throw apiError(e, 'Could not start category image job')
  }
}

export type AiJobStatusResponse =
  | { status: 'pending' }
  | { status: 'completed'; imageUrl: string }
  | { status: 'failed'; error: string }

export async function getStorefrontAiJob(jobId: string): Promise<AiJobStatusResponse> {
  try {
    const { data } = await api.get(`/storebusiness/storefront/ai/jobs/${encodeURIComponent(jobId)}`)
    const payload = assertSuccess(data) as Record<string, unknown>
    if (payload.status === 'pending') return { status: 'pending' }
    if (payload.status === 'completed' && typeof payload.imageUrl === 'string') {
      return { status: 'completed', imageUrl: payload.imageUrl }
    }
    if (payload.status === 'failed') {
      return { status: 'failed', error: String(payload.error ?? 'Unknown error') }
    }
    return { status: 'failed', error: 'Unexpected job response' }
  } catch (e) {
    throw apiError(e, 'Could not poll AI job')
  }
}

export type PollStorefrontAiJobOptions = {
  maxAttempts?: number
  intervalMs?: number
}

/** Poll until completed, failed, or timeout (returns pending as failed with timeout message). */
export async function pollStorefrontAiImageJob(
  jobId: string,
  options?: PollStorefrontAiJobOptions,
): Promise<AiJobStatusResponse> {
  const maxAttempts = options?.maxAttempts ?? 90
  const intervalMs = options?.intervalMs ?? 2000
  for (let i = 0; i < maxAttempts; i += 1) {
    const st = await getStorefrontAiJob(jobId)
    if (st.status !== 'pending') return st
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  return { status: 'failed', error: 'Generation timed out' }
}
